import {
  AccountInfo,
  AuthenticationResult,
  InteractionRequiredAuthError,
  PublicClientApplication
} from '@azure/msal-browser';
import { useVariable } from '@softwareimaging/backstage';
import { isPast, subSeconds } from 'date-fns';
import { createContext, FC, PropsWithChildren, useContext, useMemo } from 'react';
import { Outlet } from 'react-router';
import { createStore, StoreApi, useStore } from 'zustand';
import { NotAuthenticated } from '../../pages/authentication/NotAuthenticated';
import { Variables } from '../backstage/config';

import { useAddAuthenticatedContext, useTrackEvent } from '../insights/AppInsightsProvider';
import { useUser } from './hooks/user';
import { NotVerified } from './NotVerified';

export enum AuthenticationStatus {
  NotAuthenticated = 'NotAuthenticated',
  Authenticating = 'Authenticating',
  Authenticated = 'Authenticated',
  Error = 'Error'
}

export interface Account {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  claims: Record<string, string | string[]>;
}

interface AuthenticationInformation {
  status: AuthenticationStatus;
  account?: Account | null;
}

interface AuthenticationActions {
  onRedirect: () => Promise<string | void>;
  signInSilent: () => Promise<void>;
  signInUp: (from?: string) => Promise<void>;
  signOut: () => Promise<void>;
  getRequestToken: () => Promise<{ accessToken: string }>;
}

interface AuthenticationStore extends AuthenticationInformation, AuthenticationActions {}
const AuthenticationContext = createContext<StoreApi<AuthenticationStore> | null>(null);

const getAccountDetails = (account: AccountInfo): Account => {
  const claims = account.idTokenClaims as Record<string, string | string[]>;
  if (!claims) throw new Error('Invalid claims');
  const id = claims.sub as string;
  const firstName = claims.given_name as string;
  const lastName = claims.family_name as string;
  const [email] = claims.emails as string[];

  if (!id || !email) throw new Error('Invalid account');

  return {
    id,
    firstName,
    lastName,
    email,
    claims
  };
};

const restoreAuthentication = (instance: PublicClientApplication): AuthenticationInformation => {
  const accounts = instance.getAllAccounts();
  const account = accounts.find(account => {
    return account.idTokenClaims?.aud === instance.getConfiguration().auth.clientId;
  });
  if (account) {
    return {
      account: getAccountDetails(account),
      status: AuthenticationStatus.Authenticated
    };
  }

  return {
    status: AuthenticationStatus.NotAuthenticated
  };
};

export const AuthenticationProvider: FC<PropsWithChildren> = ({ children }) => {
  const clientId = useVariable<Variables>('clientId');
  const policy = useVariable<Variables>('policy');
  const authority = useVariable<Variables>('authority');
  const tenant = useVariable<Variables>('tenant');
  const apiClientId = useVariable<Variables>('apiClientId');
  const apiScope = useVariable<Variables>('apiScope');

  if (!clientId || !policy || !authority) throw new Error('Missing configuration');

  const trackEvent = useTrackEvent();
  const instance = useMemo(() => {
    return new PublicClientApplication({
      auth: {
        clientId,
        authority: `${authority}/${tenant}/${policy}`,
        knownAuthorities: [authority],
        redirectUri: `${window.location.origin}/auth/redirect`,
        postLogoutRedirectUri: `${window.location.origin}/auth/signout/redirect`,
        navigateToLoginRequestUrl: false
      },
      cache: {
        cacheLocation: 'sessionStorage'
      }
    });
  }, [authority, clientId, policy, tenant]);

  const addAuthContext = useAddAuthenticatedContext();

  const store = useMemo(() => {
    const restoredAuthentication = restoreAuthentication(instance);

    if (restoredAuthentication.account) {
      addAuthContext(restoredAuthentication.account.email);
    }

    return createStore<AuthenticationStore & { accessTokenResult: AuthenticationResult | null }>((set, get) => ({
      accessTokenResult: null,
      ...restoredAuthentication,
      getRequestToken: async () => {
        const { signInUp, accessTokenResult } = get();
        if (accessTokenResult && accessTokenResult.expiresOn && !isPast(subSeconds(accessTokenResult.expiresOn, 10))) {
          return { accessToken: accessTokenResult.accessToken }; // Return an object with accessToken
        }
        try {
          const [account] = instance.getAllAccounts();
          if (!account) throw new Error('No account found');

          const accessTokenResult = await instance.acquireTokenSilent({
            scopes: [`https://${tenant}/${apiClientId}/${apiScope}`],
            authority: `${authority}/${tenant}/${policy}`,
            account
          });

          set({ accessTokenResult });
          return { accessToken: accessTokenResult.accessToken };
        } catch (e) {
          if (e instanceof InteractionRequiredAuthError) {
            await signInUp();
          }
          throw e;
        }
      },
      onRedirect: async () => {
        const response = await instance.handleRedirectPromise();
        if (response && response.account) {
          const details = getAccountDetails(response.account);
          addAuthContext(details.email);

          set({
            status: AuthenticationStatus.Authenticated,
            account: details
          });

          trackEvent({
            name: 'AuthenticationSuccess',
            properties: { availableProperties: Object.keys(response.idTokenClaims) }
          });

          return response.state;
        } else {
          set({ status: AuthenticationStatus.NotAuthenticated });
        }
      },
      signInSilent: async () => {
        set({ status: AuthenticationStatus.Authenticating });
        const { account } = await instance.ssoSilent({
          scopes: ['openid', 'offline_access']
        });
        if (!account) throw new Error('No account found');
        const details = getAccountDetails(account);
        addAuthContext(details.email);
        set({
          status: AuthenticationStatus.Authenticated,
          account: details
        });
      },
      signInUp: async (from?: string) => {
        window.sessionStorage.removeItem('msal.interaction.status');
        set({ status: AuthenticationStatus.Authenticating });
        await instance.loginRedirect({
          scopes: ['openid', 'offline_access'],
          state: from
        });
      },
      signOut: async () => {
        window.sessionStorage.removeItem('msal.interaction.status');
        set({ status: AuthenticationStatus.NotAuthenticated, account: null });
        await instance.logoutRedirect();
      }
    }));
  }, [instance, authority, tenant, policy, apiScope, apiClientId]);

  return <AuthenticationContext.Provider value={store}>{children}</AuthenticationContext.Provider>;
};

type SelectorReturn<S extends (s: AuthenticationStore) => any> = ReturnType<S>;

export function useAuthentication<S extends (s: AuthenticationStore) => any>(selector: S): SelectorReturn<S> {
  const context = useContext(AuthenticationContext);
  if (!context) {
    throw new Error('useAuthentication must be used within a AuthenticationProvider');
  }
  return useStore(context, selector);
}

export const Verified: FC = () => {
  const user = useUser();
  return user.customerId ? <Outlet /> : <NotVerified />;
};

export const Authenticated: FC = () => {
  const account = useAuthentication(state => state.account);
  return account ? <Verified /> : <NotAuthenticated />;
};
